import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring, useTransition } from 'react-spring';
import styled, { css } from 'styled-components';
import illustrations from '../../../../../../../Illustration/components';
import JustificationContainer from '../../../../../../../JustificationContainer';
import { Heading2 } from '../../../../../../../Typography/index';
import Illustration from '../../../../../../../Illustration';

export type Props = {
  dataTestId?: string;
  header: React.ReactNode;
  illustration?: keyof typeof illustrations;
} & React.HtmlHTMLAttributes<HTMLDivElement>;

const ILLUSTRATION_SIZE = 50;
const HighlightedHeader: React.FC<Props> = ({
  dataTestId,
  header,
  illustration,
  ...rest
}) => {
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  const [containerSpring, containerApi] = useSpring(() => ({
    height: 'auto' as number | 'auto',
    config: { duration: 300 },
  }));

  const headerTransitions = useTransition(header, {
    from: {
      opacity: 0,
      transform: 'translateY(-100%) scale(0.5)',
    },
    enter: {
      opacity: 1,
      transform: 'translateY(0%) scale(1)',
    },
    leave: {
      opacity: 0,
      transform: 'translateY(100%) scale(0.3)',
    },
    config: {
      tension: 220,
      friction: 18,
      mass: 1,
      duration: 300,
    },
  });

  useEffect(() => {
    if (!measureRef.current) return;

    const nextHeight = measureRef.current.offsetHeight;
    if (!nextHeight) return;

    if (contentHeight === null) {
      setContentHeight(nextHeight);
      containerApi.set({ height: nextHeight });
      return;
    }

    if (nextHeight !== contentHeight) {
      setContentHeight(nextHeight);
      containerApi.start({ height: nextHeight });
    }
  }, [header, illustration, contentHeight, containerApi]);

  return (
    <Container style={containerSpring} data-testid={dataTestId} {...rest}>
      {/* Static, hidden content to reserve layout height */}
      <JustificationContainer
        align="start"
        direction="column"
        ref={measureRef}
        style={{
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <StyledIllustration name={illustration || 'bb-wc'} />
        <StyledHeading2 $margin={[null]} $size="m">
          {header}
        </StyledHeading2>
      </JustificationContainer>

      {/* Animated content absolutely positioned on top */}
      {headerTransitions((styles, _item) => (
        <AnimatedHeader style={styles}>
          <JustificationContainer align="start" direction="column">
            <StyledIllustration name={illustration || 'bb-wc'} />
            <StyledHeading2 $margin={[null]} $size="m">
              {header}
            </StyledHeading2>
          </JustificationContainer>
        </AnimatedHeader>
      ))}
    </Container>
  );
};

const Container = styled(animated.div)<{}>(
  ({ theme }) => css`
    box-sizing: content-box;
    position: relative;
    overflow: hidden;
    background-color: ${theme.color('primary')};
    padding: ${theme.space('base')};
    border-radius: ${theme.getSystem().border.radius.base}
      ${theme.getSystem().border.radius.base} 0 0;
  `,
);

const AnimatedHeader = styled(animated.div)<{}>(
  ({ theme }) => css`
    position: absolute;
    padding: ${theme.space('base')};
    inset: 0;
    display: flex;
    align-items: center;
  `,
);

const StyledHeading2 = styled(Heading2)<{}>(
  ({ theme }) => css`
    color: ${theme.color('primary', 'text')};
    font-size: ${theme.fontSize('s')};
    margin-top: ${theme.space('xs')};
  `,
);

const StyledIllustration = styled(Illustration)<{}>(
  ({ theme }) => css`
    svg {
      height: ${ILLUSTRATION_SIZE}px;
      width: ${ILLUSTRATION_SIZE}px;
      color: ${theme.color('primary', 'text')};
    }
  `,
);

export default HighlightedHeader;
